import { useRef, useState } from "react";
import { ArrowDownRight, ArrowRight, Minus, Plus, ShoppingBag } from "lucide-react";
import { ConceptBar } from "../components/ConceptBar";
import { DemoForm } from "../components/DemoForm";
import { MetricValue } from "../components/MetricValue";
import { PointerAura } from "../components/PointerAura";
import { useGsap } from "../hooks/useGsap";
import "../styles/sillage.css";

const notes = [
  {
    name: "Чёрный чай",
    description: "Густой настой без сладости: сухой, дымный и чуть минеральный."
  },
  {
    name: "Мокрый камень",
    description: "Прохладная влажность после короткого летнего дождя."
  },
  {
    name: "Ирис",
    description: "Пудровый оттенок, который смягчает тёмную древесную основу."
  },
  {
    name: "Ветивер",
    description: "Корневой, землистый шлейф с чистым графитовым краем."
  }
];

export function SillagePage() {
  const page = useRef<HTMLElement>(null);
  const [activeNote, setActiveNote] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [bagState, setBagState] = useState<"idle" | "added">("idle");

  useGsap(page, (gsap) => {
    gsap.from(".sillage-hero__copy > *", {
      opacity: 0,
      y: 30,
      duration: 1.1,
      stagger: 0.12,
      ease: "power3.out"
    });
    gsap.from(".sillage-hero__product", {
      opacity: 0,
      scale: 0.88,
      rotate: 2,
      duration: 1.35,
      ease: "power3.out"
    });
    gsap.to(".sillage-hero__product img", {
      yPercent: 10,
      scale: 1.07,
      ease: "none",
      scrollTrigger: {
        trigger: ".sillage-hero",
        start: "top top",
        end: "bottom top",
        scrub: true
      }
    });
    gsap.to(".sillage-hero__copy", {
      yPercent: 15,
      opacity: 0.3,
      ease: "none",
      scrollTrigger: {
        trigger: ".sillage-hero",
        start: "35% top",
        end: "bottom top",
        scrub: true
      }
    });
    gsap.to(".sillage-unfold__bottle img", {
      rotate: -7,
      yPercent: 7,
      scale: 1.04,
      ease: "none",
      scrollTrigger: {
        trigger: ".sillage-unfold",
        start: "top top",
        end: "bottom bottom",
        scrub: 1,
      },
    });
    gsap.utils.toArray<HTMLElement>(".sillage-unfold__scene").forEach((scene) => {
      gsap.from(scene, {
        y: 70,
        opacity: 0.18,
        duration: 0.9,
        ease: "power3.out",
        scrollTrigger: {
          trigger: scene,
          start: "top 78%",
          end: "center 52%",
          scrub: 0.45,
        },
      });
    });
    const unfoldStage = page.current?.querySelector<HTMLElement>(".sillage-unfold__stage");
    const unfoldMeter = page.current?.querySelector<HTMLElement>(".sillage-unfold__progress i");
    gsap.utils.toArray<HTMLElement>(".sillage-unfold__scene").forEach((scene, index) => {
      const activatePhase = () => {
        if (unfoldStage) unfoldStage.dataset.phase = String(index + 1);
        if (unfoldMeter) {
          gsap.to(unfoldMeter, {
            scaleY: (index + 1) / 3,
            duration: 0.7,
            ease: "power3.out",
            overwrite: "auto",
          });
        }
      };

      gsap.to(scene, {
        "--scene-focus": 1,
        ease: "none",
        scrollTrigger: {
          trigger: scene,
          start: "top 62%",
          end: "bottom 38%",
          toggleActions: "play reverse play reverse",
          onEnter: activatePhase,
          onEnterBack: activatePhase,
        },
      });
    });
    gsap.to(".sillage-particle", {
      y: (index) => (index % 2 ? -34 : 28),
      x: (index) => (index % 3 - 1) * 24,
      rotate: 180,
      duration: 3.8,
      stagger: 0.12,
      repeat: -1,
      yoyo: true,
      ease: "sine.inOut",
    });
    gsap.fromTo(
      ".sillage-ritual__image img",
      { scale: 1.14 },
      {
        scale: 1,
        scrollTrigger: {
          trigger: ".sillage-ritual",
          start: "top bottom",
          end: "bottom top",
          scrub: true
        }
      }
    );
    gsap.from(".sillage-bento > *", {
      opacity: 0,
      y: 24,
      scale: 0.97,
      stagger: 0.07,
      duration: 0.7,
      ease: "power2.out",
      scrollTrigger: {
        trigger: ".sillage-bento",
        start: "top 76%"
      }
    });
    gsap.from(".sillage-atelier__copy > *", {
      opacity: 0,
      y: 38,
      stagger: 0.08,
      duration: 0.8,
      ease: "power3.out",
      scrollTrigger: { trigger: ".sillage-atelier", start: "top 72%" },
    });
  });

  function addToBag() {
    setBagState("added");
    window.setTimeout(() => setBagState("idle"), 2400);
  }

  return (
    <main id="main" ref={page} className="sillage">
      <ConceptBar label="Нишевая парфюмерия" />
      <PointerAura color="#bc8465" className="sillage-pointer" />

      <header className="sillage-nav">
        <a className="sillage-nav__menu" href="#composition">
          Коллекция
        </a>
        <a className="sillage-logo" href="#top" aria-label="Sillage — наверх">
          SILLAGE
        </a>
        <a className="sillage-nav__bag" href="#purchase" data-magnetic>
          <ShoppingBag aria-hidden="true" /> Корзина
        </a>
      </header>

      <section className="sillage-hero" id="top" aria-labelledby="sillage-title">
        <div className="sillage-hero__copy">
          <p>Parfum d’auteur · édition 07</p>
          <h1 id="sillage-title">Запах дождя на тёплом камне</h1>
          <p className="sillage-hero__lead">
            Чёрный чай, ирис и сухой ветивер. Композиция раскрывается близко к коже и остаётся
            тихим минеральным следом
          </p>
          <div className="sillage-hero__actions">
            <a href="#purchase" className="sillage-button" data-magnetic>
              Попробовать Parfum 07 <ArrowRight aria-hidden="true" />
            </a>
            <a href="#composition" className="sillage-link">
              Исследовать композицию <ArrowDownRight aria-hidden="true" />
            </a>
          </div>
        </div>
        <div className="sillage-hero__product" data-tilt>
          <img
            src="/assets/sillage/hero.webp"
            alt="Флакон аромата на бордовом лаке среди чайных листьев и лепестков ириса"
            width="1024"
            height="1536"
            fetchPriority="high"
          />
          <span>30 ml · extrait de parfum</span>
        </div>
        <p className="sillage-hero__side">Смешано малыми партиями в ателье на Rue Bellot, 14</p>
      </section>

      <div className="sillage-marquee" aria-hidden="true">
        <div className="sillage-marquee__track">
          {[0, 1].map((group) => (
            <div className="sillage-marquee__group" key={group}>
              {[0, 1, 2, 3].flatMap((cycle) => ["BLACK TEA", "WET STONE", "IRIS", "VETIVER"].map((item) => (
                <span key={`${group}-${cycle}-${item}`}>{item}<i>◆</i></span>
              )))}
            </div>
          ))}
        </div>
      </div>

      <section className="sillage-intro">
        <p>Не пирамида нот, а три сцены.</p>
        <h2 data-split>
          Сначала — холодный воздух. Затем чай и мокрый камень. В финале остаётся сухой корень
          ветивера.
        </h2>
      </section>

      <section className="sillage-unfold" aria-label="Как раскрывается аромат">
        <div className="sillage-unfold__stage" data-phase="1">
          <p>Story of a sillage / 07</p>
          <div className="sillage-unfold__progress" aria-hidden="true">
            <span>AIR</span>
            <b><i /></b>
            <span>ROOT</span>
          </div>
          <div className="sillage-unfold__bottle">
            <img
              src="/assets/sillage/flacon.webp"
              alt="Флакон Sillage Parfum 07"
              width="1200"
              height="1800"
              loading="lazy"
            />
            {Array.from({ length: 12 }).map((_, index) => (
              <i className="sillage-particle" key={index} />
            ))}
          </div>
          <span>Флакон остаётся. Шлейф меняется.</span>
        </div>
        <div className="sillage-unfold__scenes">
          <article className="sillage-unfold__scene" data-spotlight>
            <span>00—15 min</span>
            <p>Первое движение</p>
            <h3>Холодный воздух касается кожи.</h3>
            <figure>
              <img src="/assets/sillage/ingredients.webp" alt="Ирис и ароматические ингредиенты" width="1536" height="1024" loading="lazy" />
              <figcaption>Бергамот · влажный камень · озон</figcaption>
            </figure>
          </article>
          <article className="sillage-unfold__scene" data-spotlight>
            <span>15—90 min</span>
            <p>Сердце композиции</p>
            <h3>Чай становится тёплым и дымным.</h3>
            <figure>
              <img src="/assets/sillage/blending.webp" alt="Руки парфюмера смешивают эссенции стеклянными пипетками" width="1400" height="1800" loading="lazy" />
              <figcaption>Чёрный чай · ирис · берёзовый дым</figcaption>
            </figure>
          </article>
          <article className="sillage-unfold__scene" data-spotlight>
            <span>6—10 hours</span>
            <p>То, что остаётся</p>
            <h3>Сухой минеральный след, слышимый только близко.</h3>
            <figure>
              <img src="/assets/sillage/still-life.webp" alt="Тёмный флакон, мокрый камень, чай и лепесток ириса" width="1800" height="1125" loading="lazy" />
              <figcaption>Ветивер · светлое дерево · белый мускус</figcaption>
            </figure>
          </article>
        </div>
      </section>

      <section className="sillage-composition" id="composition">
        <div className="sillage-composition__copy">
          <p>Композиция</p>
          <h2 data-split>Что слышно ближе всего</h2>
          <div className="sillage-notes" role="tablist" aria-label="Ноты аромата">
            {notes.map((note, index) => (
              <button
                key={note.name}
                type="button"
                role="tab"
                aria-selected={activeNote === index}
                onClick={() => setActiveNote(index)}
              >
                <span>{String(index + 1).padStart(2, "0")}</span>
                {note.name}
              </button>
            ))}
          </div>
        </div>
        <div className={`sillage-composition__stage sillage-composition__stage--${activeNote}`} role="tabpanel" aria-live="polite">
          <div key={activeNote}>
            <span>{String(activeNote + 1).padStart(2, "0")}</span>
            <h3>{notes[activeNote].name}</h3>
            <p>{notes[activeNote].description}</p>
          </div>
        </div>
      </section>

      <section className="sillage-ritual">
        <div className="sillage-ritual__image">
          <img
            src="/assets/sillage/organza.webp"
            alt="Скульптурная композиция из чёрной органзы, мрамора и янтарного света"
            width="1800"
            height="1200"
            loading="lazy"
          />
        </div>
        <div className="sillage-ritual__copy">
          <p>Ритуал нанесения</p>
          <h2 data-split>Тепло кожи делает аромат мягче.</h2>
          <p>
            Одного распыления достаточно. Не растирайте аромат: дайте верхним летучим оттенкам
            уйти самостоятельно, а древесной базе — проявиться через 20–30 минут.
          </p>
        </div>
      </section>

      <section className="sillage-bento" aria-label="Характеристики аромата">
        <article className="sillage-bento__main" data-spotlight>
          <p>Формула</p>
          <h2>86% ингредиентов природного происхождения.</h2>
          <span>Без красителей, силиконов и компонентов животного происхождения.</span>
        </article>
        <article data-spotlight>
          <p>Концентрация</p>
          <strong><MetricValue value={24} suffix="%" /></strong>
          <span>Extrait de parfum</span>
        </article>
        <article data-spotlight>
          <p>Партия</p>
          <strong><MetricValue value={240} /></strong>
          <span>флаконов в выпуске</span>
        </article>
        <article className="sillage-bento__quote" data-spotlight>
          <p>Характер</p>
          <blockquote>«Тихий аромат для людей, которые не хотят входить в комнату раньше себя».</blockquote>
          <span>Элиза Моро · парфюмер Sillage</span>
        </article>
      </section>

      <section className="sillage-atelier">
        <div className="sillage-atelier__image">
          <img src="/assets/sillage/atelier.webp" alt="Вечерняя работа в парфюмерном ателье Sillage" width="1800" height="1200" loading="lazy" />
        </div>
        <div className="sillage-atelier__copy">
          <p>Atelier / Paris 11e</p>
          <h2 data-split>240 флаконов. 37 ручных операций.</h2>
          <p>Каждая партия выдерживается шесть недель, фильтруется холодом и разливается в ателье без конвейера.</p>
          <dl>
            <div><dt>Мацерация</dt><dd>42 дня</dd></div>
            <div><dt>Розлив</dt><dd>Rue Bellot, 14</dd></div>
            <div><dt>Парфюмер</dt><dd>Élise Moreau</dd></div>
          </dl>
        </div>
      </section>

      <section className="sillage-purchase" id="purchase">
        <div className="sillage-purchase__visual">
          <img src="/assets/sillage/flacon.webp" alt="Флакон Sillage Parfum 07" width="1200" height="1800" loading="lazy" />
        </div>
        <div className="sillage-purchase__details">
          <p>Parfum 07 · 30 ml</p>
          <h2>Sillage after rain</h2>
          <div className="sillage-purchase__price">
            <strong>14 800 ₽</strong>
            <span>Налоги включены</span>
          </div>
          <div className="sillage-quantity" aria-label="Количество">
            <button
              type="button"
              onClick={() => setQuantity(Math.max(1, quantity - 1))}
              aria-label="Уменьшить количество"
            >
              <Minus aria-hidden="true" />
            </button>
            <output aria-live="polite">{quantity}</output>
            <button type="button" onClick={() => setQuantity(quantity + 1)} aria-label="Увеличить количество">
              <Plus aria-hidden="true" />
            </button>
          </div>
          <button className="sillage-add" type="button" onClick={addToBag} data-magnetic>
            {bagState === "added" ? "Добавлено в корзину" : "Добавить в корзину"}
          </button>
          <ul>
            <li>Бесплатная доставка от 10 000 ₽</li>
            <li>Пробник вложен в заказ до вскрытия флакона</li>
            <li>Возврат невскрытого флакона в течение 14 дней</li>
          </ul>
        </div>
      </section>

      <section className="sillage-contact">
        <div>
          <p>Private smelling</p>
          <h2>Получить приглашение на камерную презентацию.</h2>
        </div>
        <DemoForm
          title="Запросить приглашение"
          description="Мы свяжемся, чтобы предложить ближайшую дату и камерный формат встречи."
          submitLabel="Оставить запрос"
          successMessage="Запрос принят."
          fields={["name", "email"]}
        />
      </section>

      <footer className="sillage-footer">
        <a className="sillage-logo" href="#top">
          SILLAGE
        </a>
        <div>
          <a href="mailto:atelier@sillage.example">atelier@sillage.example</a>
          <p>Rue Bellot, 14 · Paris</p>
        </div>
        <p>Parfums d’auteur · depuis 2018</p>
      </footer>
    </main>
  );
}
